import type { CSSProperties } from "react";
import {
  ArrowDown, ArrowDownLeft, ArrowDownRight, ArrowLeft, ArrowRight, ArrowUp,
  ArrowUpLeft, ArrowUpRight, AudioLines, Bell, Camera, Check, CircleCheck,
  CirclePause, CirclePlay, Clapperboard, Clock, Coffee, CornerUpLeft,
  Ear, Eye, EyeOff, FastForward, Flag, GlassWater, Hand, HandMetal, Headphones, Heart,
  Lightbulb, Maximize2, Megaphone, Mic, MicOff, Minimize2, MoveHorizontal,
  MoveVertical, Music2, Octagon, Pause, PersonStanding, Play, Redo2,
  Repeat2, RotateCcw, Scissors, Smile, Square, Star, ThumbsUp,
  Timer, TriangleAlert, Turtle, Volume1, Volume2, VolumeX, Waves, Wind,
  Zap, type LucideIcon,
} from "lucide-react";

export type LaunchpadIconOption = {
  id: string;
  label: string;
  category: "Direction" | "Delivery" | "Production" | "Playback";
  Icon: LucideIcon;
};

/** Local Lucide icons (ISC, with Feather MIT portions). Saved keys store IDs, never SVG markup. */
export const launchpadIcons: LaunchpadIconOption[] = [
  { id: "hand", label: "Hand", category: "Direction", Icon: Hand },
  { id: "pause", label: "Pause", category: "Playback", Icon: Pause },
  { id: "play", label: "Play", category: "Playback", Icon: Play },
  { id: "stop", label: "Stop", category: "Playback", Icon: Square },
  { id: "restart", label: "Restart", category: "Playback", Icon: RotateCcw },
  { id: "repeat", label: "Repeat", category: "Playback", Icon: Repeat2 },
  { id: "fast-forward", label: "Fast forward", category: "Playback", Icon: FastForward },
  { id: "circle-pause", label: "Pause in circle", category: "Playback", Icon: CirclePause },
  { id: "circle-play", label: "Play in circle", category: "Playback", Icon: CirclePlay },
  { id: "timer", label: "Timer", category: "Playback", Icon: Timer },
  { id: "clock", label: "Clock", category: "Playback", Icon: Clock },
  { id: "return", label: "Return", category: "Playback", Icon: CornerUpLeft },
  { id: "next", label: "Next", category: "Playback", Icon: Redo2 },
  { id: "eye-off", label: "Blackout", category: "Production", Icon: EyeOff },
  { id: "alert", label: "Alert", category: "Production", Icon: TriangleAlert },
  { id: "camera", label: "Camera", category: "Production", Icon: Camera },
  { id: "clapperboard", label: "Clapperboard", category: "Production", Icon: Clapperboard },
  { id: "scissors", label: "Cut", category: "Production", Icon: Scissors },
  { id: "flag", label: "Flag", category: "Production", Icon: Flag },
  { id: "bell", label: "Bell", category: "Production", Icon: Bell },
  { id: "headphones", label: "Headphones", category: "Production", Icon: Headphones },
  { id: "mic", label: "Microphone", category: "Production", Icon: Mic },
  { id: "mic-off", label: "Microphone off", category: "Production", Icon: MicOff },
  { id: "music", label: "Music", category: "Production", Icon: Music2 },
  { id: "lightbulb", label: "Light", category: "Production", Icon: Lightbulb },
  { id: "octagon", label: "Stop sign", category: "Production", Icon: Octagon },
  { id: "smile", label: "Smile", category: "Delivery", Icon: Smile },
  { id: "thumbs-up", label: "Thumbs up", category: "Delivery", Icon: ThumbsUp },
  { id: "check", label: "Check", category: "Delivery", Icon: Check },
  { id: "circle-check", label: "Ready", category: "Delivery", Icon: CircleCheck },
  { id: "heart", label: "Heart", category: "Delivery", Icon: Heart },
  { id: "star", label: "Star", category: "Delivery", Icon: Star },
  { id: "turtle", label: "Slower", category: "Delivery", Icon: Turtle },
  { id: "zap", label: "Faster", category: "Delivery", Icon: Zap },
  { id: "volume-high", label: "Louder", category: "Delivery", Icon: Volume2 },
  { id: "volume-low", label: "Softer", category: "Delivery", Icon: Volume1 },
  { id: "volume-off", label: "Quiet", category: "Delivery", Icon: VolumeX },
  { id: "ear", label: "Listen", category: "Delivery", Icon: Ear },
  { id: "audio", label: "Sound", category: "Delivery", Icon: AudioLines },
  { id: "megaphone", label: "Project your voice", category: "Delivery", Icon: Megaphone },
  { id: "wind", label: "Breathe", category: "Delivery", Icon: Wind },
  { id: "waves", label: "Steady", category: "Delivery", Icon: Waves },
  { id: "coffee", label: "Break", category: "Delivery", Icon: Coffee },
  { id: "water", label: "Water", category: "Delivery", Icon: GlassWater },
  { id: "eye", label: "Look", category: "Direction", Icon: Eye },
  { id: "person", label: "Standing person", category: "Direction", Icon: PersonStanding },
  { id: "hand-metal", label: "Raised fingers", category: "Direction", Icon: HandMetal },
  { id: "arrow-up", label: "Up", category: "Direction", Icon: ArrowUp },
  { id: "arrow-down", label: "Down", category: "Direction", Icon: ArrowDown },
  { id: "arrow-left", label: "Left", category: "Direction", Icon: ArrowLeft },
  { id: "arrow-right", label: "Right", category: "Direction", Icon: ArrowRight },
  { id: "arrow-up-left", label: "Up and left", category: "Direction", Icon: ArrowUpLeft },
  { id: "arrow-up-right", label: "Up and right", category: "Direction", Icon: ArrowUpRight },
  { id: "arrow-down-left", label: "Down and left", category: "Direction", Icon: ArrowDownLeft },
  { id: "arrow-down-right", label: "Down and right", category: "Direction", Icon: ArrowDownRight },
  { id: "move-horizontal", label: "Move sideways", category: "Direction", Icon: MoveHorizontal },
  { id: "move-vertical", label: "Move vertically", category: "Direction", Icon: MoveVertical },
  { id: "closer", label: "Closer", category: "Direction", Icon: Minimize2 },
  { id: "further", label: "Further away", category: "Direction", Icon: Maximize2 },
];

export type CuePictureOption = {
  id: string;
  label: string;
  category: "Playback" | "Delivery" | "Position" | "Production";
  icon?: string;
  secondary?: string;
  numeral?: string;
};

/** High-contrast studio signs, distinct from the operator key's chosen icon. */
export const launchpadPictures: CuePictureOption[] = [
  { id: "none", label: "Text only", category: "Production" },
  { id: "hold", label: "Please hold", category: "Playback", icon: "hand", secondary: "pause" },
  { id: "raise-hand", label: "Raise your hand", category: "Position", icon: "hand", secondary: "arrow-up" },
  { id: "restart-section", label: "Restart section", category: "Playback", icon: "restart" },
  { id: "slower", label: "Speak slower", category: "Delivery", icon: "turtle" },
  { id: "faster", label: "Speak faster", category: "Delivery", icon: "fast-forward" },
  { id: "louder", label: "Speak louder", category: "Delivery", icon: "volume-high", secondary: "arrow-up" },
  { id: "softer", label: "Speak softer", category: "Delivery", icon: "volume-low", secondary: "arrow-down" },
  { id: "look-camera", label: "Look at the lens", category: "Delivery", icon: "camera", secondary: "eye" },
  { id: "smile", label: "Smile", category: "Delivery", icon: "smile" },
  { id: "stand", label: "Stand up", category: "Position", icon: "person", secondary: "arrow-up" },
  { id: "sit", label: "Sit down", category: "Position", icon: "person", secondary: "arrow-down" },
  { id: "move-left", label: "Move to your left", category: "Position", icon: "person", secondary: "arrow-left" },
  { id: "move-right", label: "Move to your right", category: "Position", icon: "person", secondary: "arrow-right" },
  { id: "chin-up", label: "Chin up", category: "Position", icon: "smile", secondary: "arrow-up" },
  { id: "chin-down", label: "Chin down", category: "Position", icon: "smile", secondary: "arrow-down" },
  { id: "closer", label: "Move closer", category: "Position", icon: "closer" },
  { id: "step-back", label: "Step back", category: "Position", icon: "further" },
  { id: "water", label: "Water break", category: "Production", icon: "water" },
  { id: "mic-check", label: "Check microphone", category: "Production", icon: "mic", secondary: "check" },
  { id: "quiet", label: "Quiet on set", category: "Production", icon: "volume-off" },
  { id: "applause", label: "Applause", category: "Production", icon: "hand", secondary: "star" },
  { id: "three", label: "Three", category: "Playback", numeral: "3" },
  { id: "two", label: "Two", category: "Playback", numeral: "2" },
  { id: "one", label: "One", category: "Playback", numeral: "1" },
  { id: "action", label: "Action", category: "Playback", icon: "clapperboard", secondary: "play" },
  { id: "cut", label: "Cut", category: "Playback", icon: "scissors" },
  { id: "wrap", label: "That's a wrap", category: "Production", icon: "clapperboard", secondary: "check" },
  { id: "breathe", label: "Take a breath", category: "Delivery", icon: "wind" },
  { id: "repeat", label: "Repeat that line", category: "Playback", icon: "repeat" },
  { id: "listen", label: "Listen to direction", category: "Delivery", icon: "ear" },
  { id: "ready", label: "Ready", category: "Production", icon: "thumbs-up" },
  { id: "alert", label: "Attention", category: "Production", icon: "alert" },
  { id: "break", label: "Take a break", category: "Production", icon: "coffee", secondary: "pause" },
  { id: "energy", label: "More energy", category: "Delivery", icon: "zap" },
  { id: "steady", label: "Keep a steady pace", category: "Delivery", icon: "waves" },
  { id: "finished", label: "Finished", category: "Playback", icon: "circle-check" },
];

const iconsById = new Map(launchpadIcons.map((icon) => [icon.id, icon]));
const picturesById = new Map(launchpadPictures.map((picture) => [picture.id, picture]));

export function LaunchpadIcon({ id, size = 24, className }: {
  id: string;
  size?: number;
  className?: string;
}) {
  const Icon = iconsById.get(id)?.Icon ?? Flag;
  return <Icon size={size} className={className} strokeWidth={1.8} aria-hidden="true" focusable="false" />;
}

export function cuePictureLabel(id: string) {
  return picturesById.get(id)?.label ?? "Studio cue";
}

/** SVG signs stay crisp on the virtual prompt canvas and in small picker previews. */
export function CuePicture({ id, color, className = "", label }: {
  id: string;
  color?: string;
  className?: string;
  label?: string;
}) {
  if (id === "none") return null;
  const picture = picturesById.get(id) ?? picturesById.get("alert")!;
  const Primary = iconsById.get(picture.icon ?? "")?.Icon ?? Flag;
  const Secondary = iconsById.get(picture.secondary ?? "")?.Icon;
  return (
    <svg
      className={`cue-picture ${className}`}
      viewBox="0 0 160 160"
      role="img"
      aria-label={label ?? picture.label}
      data-picture-id={id}
      focusable="false"
      style={color ? { color } as CSSProperties : undefined}
    >
      {picture.numeral ? (
        <>
          <circle cx="80" cy="80" r="67" fill="none" stroke="currentColor" strokeWidth="5" />
          <text x="80" y="84" textAnchor="middle" dominantBaseline="central" fill="currentColor" fontSize="103" fontWeight="700" fontFamily="inherit">{picture.numeral}</text>
        </>
      ) : Secondary ? (
        <>
          <Primary x="8" y="30" width="102" height="102" strokeWidth="1.65" aria-hidden="true" />
          <Secondary x="109" y="54" width="45" height="45" strokeWidth="2.2" aria-hidden="true" />
        </>
      ) : (
        <Primary x="16" y="16" width="128" height="128" strokeWidth="1.6" aria-hidden="true" />
      )}
    </svg>
  );
}
